import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { SyncStatusPill, SYNC_STATUSES } from "@/components/clientmaster/SyncStatusPill";

export function XeroLinksTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState("Suggested Match");
  const [sel, setSel] = useState(new Set());
  const load = () => api.get("/client-master/xero-links").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const view = useMemo(() => rows.filter((r) => !f || r.xero_sync_status === f), [rows, f]);
  const counts = useMemo(() => rows.reduce((a, r) => ({ ...a, [r.xero_sync_status]: (a[r.xero_sync_status] || 0) + 1 }), {}), [rows]);
  const done = () => { load(); onChange(); setSel(new Set()); };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); done(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };

  return (
    <div data-testid="xero-links-tab">
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {["", ...SYNC_STATUSES].map((s) => (
          <button key={s || "all"} data-testid={`xl-filter-${(s || "all").replace(/\s+/g, "-").toLowerCase()}`} onClick={() => { setF(s); setSel(new Set()); }}
            className={`text-xs px-2.5 py-1 border rounded-sm ${f === s ? "border-[var(--navy)] bg-[var(--navy-soft)]" : "border-[var(--line)]"}`}>
            {s || "All"} <span className="font-mono-data text-[var(--ink-2)]">{s ? counts[s] || 0 : rows.length}</span>
          </button>
        ))}
        {sel.size > 0 && <button data-testid="xl-confirm-selected" onClick={() => act(() => api.post("/client-master/xero-links/confirm", { client_ids: [...sel] }), `${sel.size} links confirmed`)} className="btn-primary text-xs !py-1 ml-auto">Confirm {sel.size} selected</button>}
      </div>
      <table className="tbl tbl-ultra" data-testid="xero-links-table">
        <thead><tr>
          <th className="w-8">{f === "Suggested Match" && <input type="checkbox" data-testid="xl-select-all" checked={view.length > 0 && view.every((r) => sel.has(r.id))} onChange={(e) => setSel(e.target.checked ? new Set(view.map((r) => r.id)) : new Set())} />}</th>
          <th>Client ID</th><th>Client</th><th>Xero contact</th><th>Sync status</th><th>Not billed in Xero</th><th className="text-right">Actions</th>
        </tr></thead>
        <tbody>
          {view.map((r) => (
            <tr key={r.id} data-testid={`xl-row-${r.id}`}>
              <td>{r.xero_sync_status === "Suggested Match" && <input type="checkbox" checked={sel.has(r.id)} onChange={(e) => { const n = new Set(sel); e.target.checked ? n.add(r.id) : n.delete(r.id); setSel(n); }} />}</td>
              <td className="font-mono-data text-xs">{r.client_uid || "—"}</td>
              <td>{r.client_name}<div className="text-[10px] text-[var(--ink-2)]">{r.business_name || ""}</div></td>
              <td className="text-xs">{r.xero_contact_name || "—"}</td>
              <td><SyncStatusPill status={r.xero_sync_status} />{r.xero_sync_error && <div className="text-[10px] text-[var(--err)] mt-0.5">{r.xero_sync_error}</div>}</td>
              <td><input type="checkbox" data-testid={`xl-notbilled-${r.id}`} checked={r.xero_sync_status === "Not Billed in Xero"} onChange={(e) => act(() => api.patch(`/clients/${r.id}/xero-billing`, { not_billed_in_xero: e.target.checked }), "Updated")} /></td>
              <td className="text-right whitespace-nowrap">
                {r.xero_sync_status === "Suggested Match" && <>
                  <button data-testid={`xl-confirm-${r.id}`} onClick={() => act(() => api.post("/client-master/xero-links/confirm", { client_ids: [r.id] }), "Link confirmed")} className="btn-primary text-[11px] !py-0.5 !px-2 mr-1">Confirm</button>
                  <button data-testid={`xl-reject-${r.id}`} onClick={() => act(() => api.post(`/clients/${r.id}/xero-suggestion/reject`), "Suggestion rejected")} className="btn-ghost text-[11px] !py-0.5 !px-2">Reject</button>
                </>}
                {r.xero_sync_status === "Sync Error" && <button data-testid={`xl-retry-${r.id}`} onClick={() => act(() => api.post(`/clients/${r.id}/xero-retry`), "Retried")} className="btn-ghost text-[11px] !py-0.5 !px-2">Retry</button>}
              </td>
            </tr>
          ))}
          {!view.length && <tr><td colSpan={7} className="text-center text-xs text-[var(--ink-2)] py-8">No clients with this status.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
